const STORAGE_KEY = 'flowy_history_entries';
const LIMIT = 200;

// The first entry of a page load carries no router key of its own ('default'),
// so it is told apart by the page load it belongs to.
const pageLoadId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const read = (): Record<string, unknown> => {
    try {
        return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') ?? {};
    } catch {
        return {};
    }
};

/**
 * Remembers a value for one browser history entry (per tab, survives a reload).
 * The first call for an entry stores `current`; every later one - the same page
 * reached again through back/forward or reloaded - returns what was stored then,
 * whatever `current` has become in the meantime.
 *
 * @param locationKey the router's `location.key` of the entry
 * @param name what is remembered, unique within the entry
 */
export const rememberForHistoryEntry = <T,>(locationKey: string, name: string, current: T): T => {
    const entry = `${locationKey === 'default' ? `default-${pageLoadId}` : locationKey}:${name}`;
    const stored = read();
    if (Object.prototype.hasOwnProperty.call(stored, entry)) {
        return stored[entry] as T;
    }

    const keys = Object.keys(stored);
    keys.slice(0, Math.max(0, keys.length - LIMIT + 1)).forEach(key => delete stored[key]);
    stored[entry] = current;
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch {
        // Storage unavailable or full: the value is just not remembered.
    }

    return current;
};
